import { c as _c } from "react/compiler-runtime";
import { useSuspenseQuery } from '@tanstack/react-query';
import type { ExtractGetRoutes, RouteSegment } from '@openclassrooms/sdk';
import checkGetRetry from '../services/checkGetRetry';
import get from '../services/get';
import type { ApiRoutes, GetRequestBuilder, RequestOption } from '../types';
import useQueryKeys from './useQueryKeys';

/**
 * @deprecated
 * ### Description
 * `useEnableableSuspenseGet` has been deprecated and replaced by `useGet` with the `enabled` option.
 * Please use `useGet` instead and pass the `enabled` option to it.
 *
 * ### Example
 * ```
 * import { useGet } from '@openclassrooms/data-connect-web/hooks';
 *   const { data } = useGet('/users/:userId/expected-benefits', {
 *     routeParams: { userId: userId! },
 *     enabled: Boolean(userId),
 *   });
 * ```
 */
export default function useEnableableSuspenseGet(path, options) {
  const $ = _c(24);
  let t0;
  if ($[0] !== options) {
    t0 = options || {};
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let customQueryKey;
  let enabled;
  let otherOptions;
  let t1;
  if ($[2] !== t0) {
    ({
      watchedQueryKeys: t1,
      queryKey: customQueryKey,
      enabled,
      ...otherOptions
    } = t0);
    $[2] = t0;
    $[3] = customQueryKey;
    $[4] = enabled;
    $[5] = otherOptions;
    $[6] = t1;
  } else {
    customQueryKey = $[3];
    enabled = $[4];
    otherOptions = $[5];
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const watchedQueryKeys = t2;
  const t3 = enabled ? 1 : 0;
  let t4;
  if ($[9] !== otherOptions || $[10] !== t3) {
    t4 = {
      ...otherOptions,
      enabled: t3
    };
    $[9] = otherOptions;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== customQueryKey || $[13] !== path || $[14] !== t4 || $[15] !== watchedQueryKeys) {
    t5 = {
      path,
      customQueryKey,
      options: t4,
      watchedQueryKeys
    };
    $[12] = customQueryKey;
    $[13] = path;
    $[14] = t4;
    $[15] = watchedQueryKeys;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const queryKey = useQueryKeys(t5);
  let t6;
  if ($[17] !== enabled || $[18] !== otherOptions || $[19] !== path) {
    t6 = () => enabled ? get(path, otherOptions as any) : null;
    $[17] = enabled;
    $[18] = otherOptions;
    $[19] = path;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== queryKey || $[22] !== t6) {
    t7 = {
      queryKey,
      queryFn: t6,
      staleTime: 5000,
      retry: checkGetRetry
    };
    $[21] = queryKey;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return useSuspenseQuery(t7);
}